import React, { Fragment } from 'react';
import { Printer, ArrowRight, Map, UserX, Users, Layers, Crown, Zap, Settings2, UserCheck, TrendingUp } from 'lucide-react';
const PHASES = [{
  icon: Settings2,
  title: 'Build the systems & infrastructure',
  desc: 'Comp plans, CRM and tracking, onboarding, scripts, and KPI dashboards. The operating system of a real channel.'
}, {
  icon: UserCheck,
  title: 'Find the field sales leader',
  desc: 'If you don\u2019t already have one, we recruit the operator who owns the field and drives the numbers.'
}, {
  icon: Users,
  title: 'Build the team',
  desc: 'Our recruiting engine staffs reps predictably, month after month, so the channel fills fast.'
}, {
  icon: TrendingUp,
  title: 'Scale',
  desc: 'Expand into new markets, layer in leadership, and compound output as the channel matures.'
}];
const PROBLEMS = [{
  icon: Map,
  title: 'No proven playbook',
  desc: 'Building field sales by trial and error burns cash and time you do not need to spend.'
}, {
  icon: UserX,
  title: 'No leader to own it',
  desc: 'Without the right field operator, a new channel never gets off the ground.'
}, {
  icon: Users,
  title: 'Can\u2019t staff the team',
  desc: 'Recruiting can\u2019t keep reps in the field fast enough to build real momentum.'
}];
const VALUE_PROPS = [{
  icon: Layers,
  title: 'A complete channel infrastructure',
  desc: 'Everything the channel runs on, built and installed for you, not pieced together over months.'
}, {
  icon: Crown,
  title: 'The right field sales leader',
  desc: 'We find and vet the operator who can own the field and hit the number from day one.'
}, {
  icon: Users,
  title: 'A predictable rep pipeline',
  desc: 'The same recruiting system behind 5,000+ door-to-door hires, pointed at your new channel.'
}, {
  icon: Zap,
  title: 'Speed to traction',
  desc: 'A launch sequenced to gain momentum quickly instead of stalling out in the first quarter.'
}];
const STATS = [{
  value: '5,000+',
  label: 'door-to-door hires'
}, {
  value: '21',
  label: 'D2D industries served'
}, {
  value: '15+',
  label: 'new market launches'
}, {
  value: '60+',
  label: 'active partnerships'
}];
export function D2DLaunchOnePager() {
  return <div className="onepager-print-root min-h-screen w-full bg-slate-200 py-6 px-3 sm:py-10 sm:px-6 print:p-0 print:bg-white" data-id="element-814" data-mp="3bt2bnuf2zbda">
      {/* Action bar (screen only) */}
      <div className="no-print max-w-[8.5in] mx-auto mb-4 flex items-center justify-between" data-id="element-815" data-mp="37tthwpk1jdhq">
        <p className="text-sm text-slate-500" data-id="element-816" data-mp="32yw0ovclxxtr">
          Print or save as PDF to send as a leave-behind.
        </p>
        <button onClick={() => window.print()} className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#163A5F] hover:bg-[#0f2a47] text-white font-semibold text-sm transition-colors shadow-sm" data-id="element-817" data-mp="2y02uzp3kbb45">
          <Printer className="w-4 h-4 mr-2" data-id="element-818" data-mp="1x4twcteb3yz" />
          Print / Save as PDF
        </button>
      </div>

      {/* The printable sheet */}
      <article className="onepager-sheet mx-auto w-full max-w-[8.5in] bg-white text-slate-800 shadow-xl rounded-lg overflow-hidden" data-id="element-819" data-mp="6vy5fzp4gkwi">
        {/* Header band */}
        <header className="bg-[#163A5F] text-white px-8 sm:px-10 py-7 print:py-4 flex items-start justify-between" data-id="element-820" data-mp="fwcvfcd16o6c">
          <div data-id="element-821" data-mp="kueehxaginht">
            <div className="flex items-center gap-2.5" data-id="element-822" data-mp="kuo2xgfusmle">
              <span className="text-3xl print:text-2xl font-extrabold tracking-tight" data-id="element-823" data-mp="ptqxu824dcfn">
                HIRE
              </span>
              <span className="h-6 w-px bg-white/30" data-id="element-824" data-mp="umbqh69ahjhl" />
              <span className="text-sm font-medium text-[#7FE0C3] tracking-wide uppercase" data-id="element-825" data-mp="zju3p8ucakve">
                D2D Launch
              </span>
            </div>
            <p className="mt-2 print:mt-1 text-sm text-slate-300 max-w-md leading-relaxed" data-id="element-826" data-mp="13nib9riz1ggn">
              Launching door-to-door as a new distribution channel.
            </p>
          </div>
          <div className="text-right text-xs text-slate-300 leading-relaxed hidden sm:block" data-id="element-827" data-mp="uwi3ig3hl3q3">
            <p className="font-semibold text-white" data-id="element-828" data-mp="qvod9g2kncu1">d2dhire.com</p>
            <p className="text-[#7FE0C3] font-medium" data-id="element-829" data-mp="14oe2dtdecxvd">Predictable Recruiting</p>
          </div>
        </header>

        <div className="onepager-body px-8 sm:px-10 py-8 print:py-4 space-y-7 print:space-y-3.5" data-id="element-830" data-mp="zoi141w33kji">
          {/* Hero */}
          <section data-id="element-831" data-mp="bzpa8jubk2vr">
            <h1 className="text-[1.7rem] sm:text-4xl print:text-[1.5rem] font-extrabold text-[#163A5F] leading-tight print:leading-[1.15]" data-id="element-832" data-mp="c19v1hqu07gf">
              Launch door-to-door as a
              <span className="text-[#15b48a]" data-id="element-833" data-mp="lyxzize8otbs">
                {' '}
                channel built for traction.
              </span>
            </h1>
            <p className="mt-3 print:mt-1.5 text-[15px] sm:text-base print:text-[11.5px] text-slate-600 leading-relaxed max-w-3xl" data-id="element-834" data-mp="1xc9yavv56lr3">
              You already have a healthy business. Adding a door-to-door channel
              is one of the fastest ways to grow it, but only if it is built
              right. We stand up the entire channel from scratch, the systems,
              the leadership, and the team, and sequence the launch so it gains
              traction quickly instead of stalling out.
            </p>
          </section>

          {/* Problem */}
          <section data-id="element-835" data-mp="36bkvaj215jq4">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#15b48a] mb-3 print:mb-2" data-id="element-836" data-mp="3tz1aql475jzn">
              Why most D2D launches stall
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 print:gap-2.5" data-id="element-837" data-mp="1ugz6gr5mq27">
              {PROBLEMS.map(p => <div key={p.title} className="rounded-lg border border-slate-200 bg-slate-50 p-4 print:p-2.5" data-id="element-838" data-mp="3k7f30yr2i31n">
                  <div className="flex items-center justify-center w-9 h-9 print:w-7 print:h-7 rounded-md bg-[#163A5F]/5 text-[#163A5F] mb-2.5 print:mb-1.5" data-id="element-839" data-mp="gqyax939a6pa">
                    <p.icon className="w-5 h-5 print:w-4 print:h-4" data-id="element-840" data-mp="vfrmig5k8p71" />
                  </div>
                  <h3 className="text-sm font-bold text-[#163A5F] mb-1 leading-snug" data-id="element-841" data-mp="qgepx3ow03r9">
                    {p.title}
                  </h3>
                  <p className="text-[12.5px] print:text-[10.5px] text-slate-500 leading-relaxed" data-id="element-842" data-mp="lfqw6a99x8tc">
                    {p.desc}
                  </p>
                </div>)}
            </div>
          </section>

          {/* The build sequence */}
          <section data-id="element-843" data-mp="7px6xqtuwmj3">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#15b48a] mb-3 print:mb-2" data-id="element-844" data-mp="7om6o0zmdwwq">
              How we build the channel
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 print:gap-2.5" data-id="element-845" data-mp="3twvjyyw9ea69">
              {PHASES.map((phase, idx) => <Fragment key={phase.title} data-id="element-846" data-mp="3oy0pgzoxix6b" data-mpi={idx}>
                  <div className="relative rounded-lg border border-slate-200 p-4 print:p-2.5" data-id="element-847" data-mp="3ozvccjjrgzd1">
                    <div className="flex items-center gap-2 mb-2 print:mb-1.5" data-id="element-848" data-mp="3kwyxn3xcdned">
                      <span className="flex items-center justify-center w-6 h-6 print:w-5 print:h-5 rounded-full bg-[#163A5F] text-white text-[11px] font-bold flex-shrink-0" data-id="element-849" data-mp="20dlg6ic72smq">
                        {idx + 1}
                      </span>
                      <phase.icon className="w-4 h-4 text-[#15b48a]" data-id="element-850" data-mp="29d01z4hf2f0w" />
                    </div>
                    <h3 className="text-[13px] font-bold text-[#163A5F] mb-1 leading-snug" data-id="element-851" data-mp="2ebjs7h31mcz2">
                      {phase.title}
                    </h3>
                    <p className="text-[11.5px] print:text-[10px] text-slate-500 leading-relaxed" data-id="element-852" data-mp="1lq0e8rr22zge">
                      {phase.desc}
                    </p>
                  </div>
                </Fragment>)}
            </div>
          </section>

          {/* What we build for you */}
          <section data-id="element-853" data-mp="xyuysdp1296m">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#15b48a] mb-3 print:mb-2" data-id="element-854" data-mp="xze670h1xf6e">
              What we build for you
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 print:gap-x-5 print:gap-y-2.5" data-id="element-855" data-mp="3sg14811ybgxl">
              {VALUE_PROPS.map(v => <div key={v.title} className="flex items-start gap-3" data-id="element-856" data-mp="3njkej2f3suie">
                  <div className="flex items-center justify-center w-9 h-9 print:w-7 print:h-7 rounded-md bg-[#15b48a]/10 text-[#15b48a] flex-shrink-0" data-id="element-857" data-mp="5cwbrj7j0jfw">
                    <v.icon className="w-5 h-5 print:w-4 print:h-4" data-id="element-858" data-mp="faaq9d9w2y5k" />
                  </div>
                  <div data-id="element-859" data-mp="f7y40nx3m1mp">
                    <h3 className="text-sm font-bold text-[#163A5F] leading-snug" data-id="element-860" data-mp="abpsrlm0uskt">
                      {v.title}
                    </h3>
                    <p className="text-[12.5px] print:text-[10.5px] text-slate-500 leading-relaxed" data-id="element-861" data-mp="o230pnywy7a5">
                      {v.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </section>

          {/* Proof */}
          <section className="rounded-lg bg-[#163A5F] px-6 py-5 print:py-3.5 print:px-5" data-id="element-862" data-mp="t2qlvt0gntwg">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 print:gap-2" data-id="element-863" data-mp="1cn7ozm4pzhmv">
              {STATS.map(s => <div key={s.label} className="text-center" data-id="element-864" data-mp="1hl982725bgyc">
                  <div className="text-2xl sm:text-3xl print:text-xl font-extrabold text-[#7FE0C3] leading-none" data-id="element-865" data-mp="12pjqcl3ztpzf">
                    {s.value}
                  </div>
                  <div className="mt-1.5 print:mt-1 text-[10.5px] print:text-[9px] uppercase tracking-wide text-slate-300 leading-tight" data-id="element-866" data-mp="17qh7cud9odzc">
                    {s.label}
                  </div>
                </div>)}
            </div>
            <p className="mt-4 print:mt-2.5 text-center text-[12.5px] print:text-[10.5px] text-slate-300 leading-relaxed" data-id="element-867" data-mp="jzla3hq1kqkp">
              We have launched and scaled door-to-door channels across 21
              industries, from solar and roofing to pest control, windows, and
              home services, in new markets and established ones alike.
            </p>
          </section>
        </div>
      </article>
    </div>;
}